import cn from 'classnames';
import Image from 'next/future/image';
import { PimProductT } from '@lambda/apis/src/dynamoDb/types';
import { ReebeloConditions, ReebeloLanguage } from '@lambda/reebelo';
import { uniq } from 'lodash-es';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import { sortByBatteryHealth } from '@lambda/commons/sort';
import { ConditionPopup } from '@/components/products/conditionPopup/ConditionPopup';
import ProductVariants, { filteredVariants } from './ProductVariants';
import Variant from '../Variant';
import infoIconBlack from '@/public/icons/product/infoIconBlack.svg';
import premiumWhite from '@/public/icons/product/premium_white.svg';
import premiumBlack from '@/public/icons/product/premium_black.svg';
import premiumBg from '@/public/icons/product/premium_bg.svg';
import premiumKing from '@/public/icons/product/premium_king.svg';
import premiumShiny from '@/public/icons/product/premium_shiny.svg';
import { priceFormater } from '@/settings';
import {
  getBatteryHealthVariantPrice,
  getSuggestedPrice,
  getVariantPrice,
} from './helpers';
import { priceFormatSuperscript } from '@/lib/price';
import WarrantySlideOver from '../WarrantySlideOver';
import ReebeloCareSection from '@/components/commons/ReebeloCareSection';
import {
  Conditions,
  OfferTree,
  ReplacedProduct,
  SelectionT,
  isNaStore,
} from '@/lib/collections/catalogue/helpers';
import { DynamicVariantGroup } from './types';
import {
  generateBatteryHealthDescription,
  generateBatteryHealthTitle,
} from '@/lib/collections/helpers';
import BatteryHealthModal from './BatteryHealthModal';
import LaptopProductVariants from './LaptopProductVariants';

const PremiumFeature = ({
  title,
  iconSrc,
}: {
  title: string;
  iconSrc: string;
}) => (
  // eslint-disable-next-line tailwindcss/enforces-negative-arbitrary-values
  <div className="-ml-[7px] flex items-center justify-start">
    <div className="w-[28px]">
      <Image
        src={iconSrc}
        alt={title}
        width={25}
        height={20}
        className="h-full w-full object-contain"
        priority={true}
      />
    </div>
    <span className="ml-[3px] text-[11px] sm:ml-1 sm:text-xs">{title}</span>
  </div>
);

const CONDITIONS_INDEX_TO_PREMIUM_ARROW_POSITION: Record<number, string> = {
  0: 'left-[8%] -translate-x-[8%]',
  1: 'left-[29%] -translate-x-[29%]',
  2: 'left-1/2 -translate-x-1/2',
  3: 'left-[72%] -translate-x-[72%]',
  4: 'left-[93%] -translate-x-[93%]',
  5: 'left-1/2 -translate-x-1/2',
};

const CONDITIONS_INDEX_TO_PREMIUM_ARROW_COLOR: Record<number, string> = {
  0: 'border-b-[#222628]',
  1: 'border-b-[#222628]',
  2: 'border-b-[#222628]',
  3: 'border-b-[#233a41]',
  4: 'border-b-[#234953]',
  5: 'border-b-[#222628]',
};

type VariantSelectionSectionProps = {
  selection: SelectionT;
  isConditionOpen: boolean;
  setIsConditionOpen: Dispatch<SetStateAction<boolean>>;
  product: PimProductT;
  offerTree: OfferTree;
  language: ReebeloLanguage;
  colors: Record<string, string>;
  showCarrier: string | false | undefined;
  isUnfitProduct: boolean;
  dynamicVariantGroups: DynamicVariantGroup[] | null;
  deal: ReplacedProduct | undefined;
  offer: ReplacedProduct;
  productWarranty: WarrantyT | undefined;
  handleWarrantyClick: () => void;
  warrantyID: number | null;
  offers: ReplacedProduct[];
  categoryPk: string;
};

const VariantSelectionSection = (props: VariantSelectionSectionProps) => {
  const {
    selection,
    isConditionOpen,
    setIsConditionOpen,
    product,
    offerTree,
    language,
    colors,
    showCarrier,
    isUnfitProduct,
    dynamicVariantGroups,
    deal,
    offer,
    productWarranty,
    handleWarrantyClick,
    warrantyID,
    offers,
    categoryPk,
  } = props;
  const [suggestedVariant, setSuggestedVariant] = useState('');
  const [isBatteryHealthVisible, setIsBatteryHealthVisible] = useState(false);

  const isSmartwatch = product.category === 'Smartwatches';
  const isLaptop =
    product.category === 'Laptops' ||
    product.categoryPks?.includes('05t394s0h4e1') ||
    product.category === '05t394s0h4e1'; // To make sure we handle mapping issues correctly :)

  const getVendor = (condition: string) => {
    const variantOffer = offerTree.get({
      ...selection,
      condition,
    });

    if (variantOffer == null) return null;

    return variantOffer.vendor;
  };

  const conditionQuery = isSmartwatch
    ? {
        watchCaseMaterial: selection.watchCaseMaterial,
        watchFaceSize: selection.watchFaceSize,
        connectivity: selection.connectivity,
        watchBandMaterial: selection.watchBandMaterial,
        watchBandType: selection.watchBandType,
        watchBandColor: selection.watchBandColor,
        watchCaseColor: selection.watchCaseColor,
      }
    : {
        serviceProvider: selection.serviceProvider,
        screenSize: selection.screenSize,
        connectivity: selection.connectivity,
        storage: selection.storage,
        color: selection.color,
        cpu: selection.cpu,
        ram: selection.ram,
        simSlot: selection.simSlot,
        charger: selection.charger,
        touchbar: selection.touchbar,
        version: selection.version,
        language: selection.language,
      };

  let CONDITIONS = Object.values(Conditions[language])
    .filter((cond) => !['As New', 'Very Good'].includes(cond))
    .reverse();

  // exclude  Brand New in US & CA
  if (isNaStore) CONDITIONS = CONDITIONS.filter((c) => c !== 'Brand New');
  const uniqueInStockConditions = uniq(
    offers.filter((o) => !o.replacedFromCondition).map((o) => o.condition),
  );

  CONDITIONS = CONDITIONS.filter((c) =>
    uniqueInStockConditions.includes(c as ReebeloConditions | undefined),
  );
  const fullConditionPreset = CONDITIONS.length === 6;
  const premiumIndex = CONDITIONS.indexOf('Premium');

  const filteredOfferTree = useMemo(
    () => offerTree.filter({ serviceProvider: selection.serviceProvider }),
    [offerTree, selection.serviceProvider],
  );

  const batteryHealthes = filteredVariants(
    product.batteryHealth,
    filteredOfferTree,
  );

  return (
    <div className="mb-4 rounded-md border-gray-200 p-0 lg:border lg:p-4">
      <ConditionPopup
        activeCondition={
          selection?.condition?.replace('Very Good', 'Good') ?? ''
        }
        isOpen={isConditionOpen}
        onClose={() => setIsConditionOpen(false)}
        categoryPk={categoryPk}
      />
      <BatteryHealthModal
        isVisible={isBatteryHealthVisible}
        hideModal={() => setIsBatteryHealthVisible(false)}
      />

      {isLaptop ? (
        <LaptopProductVariants
          filteredOfferTree={filteredOfferTree}
          suggestedVariant={suggestedVariant}
          setSuggestedVariant={setSuggestedVariant}
          product={product}
          selection={selection}
          language={language}
          colors={colors}
          isUnfitProduct={isUnfitProduct}
        />
      ) : (
        <ProductVariants
          filteredOfferTree={filteredOfferTree}
          suggestedVariant={suggestedVariant}
          setSuggestedVariant={setSuggestedVariant}
          product={product}
          selection={selection}
          offerTree={offerTree}
          language={language}
          colors={colors}
          showCarrier={showCarrier}
          isUnfitProduct={isUnfitProduct}
          dynamicVariantGroups={dynamicVariantGroups}
        />
      )}

      <Variant.Group
        suggestedVariant={suggestedVariant}
        isDeal={!!deal}
        psku={product.psku}
        className={cn('grid grid-cols-5', {
          '!grid-cols-3': fullConditionPreset,
        })}
        title={
          <>
            Condition
            <button
              className="-mt-1 ml-1.5 items-center xs:-mt-0.5"
              onClick={() => setIsConditionOpen(true)}
            >
              <Image
                src={infoIconBlack}
                alt="Condition"
                width={12}
                height={12}
                className="object-contain"
              />
            </button>
          </>
        }
        id="e2e-product-condition"
      >
        {CONDITIONS.map((condition) => ({
          condition,
          price: getVariantPrice(condition, selection, offerTree),
          vendor: getVendor(condition),
          query: offerTree
            .filter({ serviceProvider: selection.serviceProvider })
            .getFrom({ condition, ...conditionQuery }, selection),
        })).map(({ condition, price, query }) => {
          const conditionActive = selection.condition === condition;

          const isPremiumCondition = condition === 'Premium';
          const suggestedPrice = getSuggestedPrice(
            query,
            offerTree.filter({ serviceProvider: selection.serviceProvider }),
          );

          return (
            <Variant.Anchor
              variant="condition"
              setSuggestedVariant={setSuggestedVariant}
              disabled={isUnfitProduct}
              isConditionVariant
              isDeal={!!deal && deal.condition === condition}
              key={condition}
              query={query}
              offerCondition={condition}
              isPremiumCondition={isPremiumCondition}
              className={cn(
                'relative h-[46px] flex-col sm:h-auto',
                {
                  '!items-center !py-2 xs:!items-start': fullConditionPreset,
                },
                {
                  '!items-center !py-1.5 sm:!items-start': !fullConditionPreset,
                },
              )}
              active={conditionActive}
              data-replace-from-condition={query?.replacedFromCondition}
            >
              <div
                className={cn(
                  'flex w-full items-center justify-center',
                  {
                    'xs:items-start xs:justify-between': fullConditionPreset,
                  },
                  {
                    'sm:-ml-1.5 sm:items-start sm:justify-between':
                      !fullConditionPreset,
                  },
                )}
              >
                <p className="!text-[10px] sm:!text-[11px]">{condition}</p>
                {isPremiumCondition && (
                  <div
                    className={cn(
                      'absolute -top-2 ml-1 h-[16px] w-[16px] shrink-0 rounded-full border border-black',
                      {
                        'xs:relative xs:left-auto xs:top-auto xs:h-[16px] xs:w-[16px] xs:border-0':
                          fullConditionPreset,
                        'sm:relative sm:left-auto sm:top-auto sm:h-[16px] sm:w-[16px] sm:border-0 lg:h-[12px] lg:w-[12px] xl:h-[16px] xl:w-[16px]':
                          !fullConditionPreset,
                        '-left-[3px] xs:left-[5px] sm:left-auto':
                          !!deal && deal.condition === condition,
                      },
                    )}
                  >
                    <Image
                      src={conditionActive ? premiumWhite : premiumBlack}
                      alt="Premium Condition"
                      width={12}
                      height={12}
                      className={cn(
                        'hidden h-full w-full object-contain',
                        {
                          'xs:block': fullConditionPreset,
                        },
                        {
                          'sm:block': !fullConditionPreset,
                        },
                      )}
                    />
                    <Image
                      src={premiumWhite}
                      alt="Premium Condition"
                      width={12}
                      height={12}
                      className={cn(
                        'block h-full w-full object-contain',
                        {
                          'xs:hidden': fullConditionPreset,
                        },
                        {
                          'sm:hidden': !fullConditionPreset,
                        },
                      )}
                    />
                  </div>
                )}
              </div>
              {query != null &&
                price != null &&
                !isUnfitProduct &&
                query?.condition === condition && (
                  <p
                    className={cn('mt-[3px] font-normal', {
                      'sm:-ml-1.5': !fullConditionPreset,
                    })}
                  >
                    {priceFormatSuperscript(
                      priceFormater.short(
                        query.suggested && suggestedPrice
                          ? suggestedPrice
                          : price,
                      ),
                    )}
                  </p>
                )}
              {(query == null ||
                isUnfitProduct ||
                query?.condition !== condition) && (
                <p
                  className={cn('mt-[3px] font-normal', {
                    'sm:-ml-1.5': !fullConditionPreset,
                  })}
                >
                  Sold Out
                </p>
              )}
            </Variant.Anchor>
          );
        })}
      </Variant.Group>
      {/* Premium Card */}
      {selection.condition === 'Premium' && (
        <div
          className="relative my-4 rounded border-2 border-black text-white sm:mt-6"
          // eslint-disable-next-line react/forbid-dom-props
          style={{
            background:
              'linear-gradient(92.65deg, #222122 38.4%, #13505B 97.23%)',
          }}
        >
          <div
            // dynamic get arrow position based on condition length
            className={cn(
              'absolute -top-[13px] left-1/2 -translate-x-1/2 border-b-[13px] border-l-[10px] border-r-[10px] border-b-[#000] border-l-transparent border-r-transparent sm:-top-[18px] sm:border-b-[18px] sm:border-l-[15px] sm:border-r-[15px]',
              CONDITIONS_INDEX_TO_PREMIUM_ARROW_POSITION[premiumIndex],
            )}
          ></div>
          <div
            // dynamic get arrow position and color based on condition length
            className={cn(
              'absolute -top-[11px] left-1/2 -translate-x-1/2 border-b-[13px] border-l-[9px] border-r-[9px] border-l-transparent border-r-transparent sm:-top-[16px] sm:border-b-[18px] sm:border-l-[14px] sm:border-r-[14px]',
              CONDITIONS_INDEX_TO_PREMIUM_ARROW_POSITION[premiumIndex],
              CONDITIONS_INDEX_TO_PREMIUM_ARROW_COLOR[premiumIndex],
            )}
          ></div>
          <div className="relative overflow-hidden px-4 py-3 xxs:px-6 xxs:py-4 sm:px-6 sm:py-4">
            <div className="absolute -bottom-1 right-[-5px] h-[40%] xs:h-[50%] sm:h-[70%] xl:h-[90%]">
              <Image
                src={premiumBg}
                alt="Premium Condition"
                priority={true}
                className="h-full w-full object-contain"
              />
            </div>
            <div className="w-[95%] xs:w-[70%] sm:w-3/5">
              <h3 className="text-sm font-bold sm:text-base">
                Premium Refurbished. Long-Lasting.
              </h3>
              <div className="[&>:not(:last-child)]:mb-[2px] [&>:not(:last-child)]:sm:mb-[4px] mt-2 sm:mt-3">
                <PremiumFeature
                  title="Top 5% of our product range"
                  iconSrc={premiumKing}
                />
                <PremiumFeature
                  title="Looks & works like new"
                  iconSrc={premiumShiny}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <Variant.Group
        title={
          <>
            Battery Health Guaranteed
            <button
              className="ml-1.5 items-center"
              onClick={() => setIsBatteryHealthVisible(true)}
            >
              <Image
                src={infoIconBlack}
                alt="Condition"
                width={12}
                height={12}
                className="-mt-0.5 object-contain"
              />
            </button>
          </>
        }
        psku={product.psku}
        className="flex"
        id="e2e-product-batteryHealth"
        suggestedVariant={suggestedVariant}
      >
        {batteryHealthes.sort(sortByBatteryHealth).map((e) => {
          const query = offerTree
            .filter({ serviceProvider: selection.serviceProvider })
            .getFrom(
              {
                ...conditionQuery,
                condition: selection.condition,
                batteryHealth: e,
              },
              selection,
            );

          if (query?.suggested) return <></>;

          const price = getBatteryHealthVariantPrice(e, selection, offerTree);

          const suggestedPrice = getSuggestedPrice(
            query,
            offerTree.filter({ serviceProvider: selection.serviceProvider }),
          );
          const priceDifference = (suggestedPrice ?? price ?? 0) - offer.price;

          const title = generateBatteryHealthTitle(e);
          const description = generateBatteryHealthDescription(e);

          return (
            <Variant.Anchor
              disabled={isUnfitProduct}
              variant="batteryHealth"
              setSuggestedVariant={setSuggestedVariant}
              key={e}
              query={query}
              active={selection.batteryHealth === e}
              className="flex !h-12 w-full items-center justify-between px-2 sm:w-[180px]"
            >
              <div className="w-full text-left">
                <div className="flex w-full items-center justify-between gap-x-2">
                  <span className="block">{title} Battery</span>
                  {priceDifference !== 0 && (
                    <span className="block ">
                      {priceDifference > 0 ? '+' : ''}
                      {priceFormatSuperscript(
                        priceFormater.short(priceDifference),
                      )}
                    </span>
                  )}
                </div>
                <span className="block font-normal text-gray-400">
                  {description}
                </span>
              </div>
            </Variant.Anchor>
          );
        })}
      </Variant.Group>
      {productWarranty && (
        <div className="mt-5">
          <ReebeloCareSection
            Modal={WarrantySlideOver}
            price={productWarranty.price}
            onClick={handleWarrantyClick}
            isActive={warrantyID !== null}
            imageUrl={offer.imageSrc}
            duration={productWarranty.duration}
          />
        </div>
      )}
    </div>
  );
};

export default VariantSelectionSection;
